import { useCallback, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import moment from "moment";
import TabHeader from "../../common/TabHeader";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import { getWriteableDate } from "../../../utils";
import { TEXT_NORMAL, TEXT_SMALL } from "../../../style";

function buildDayBoxes({ startDate, endDate, records = [] }) {
    const statusByDate = records.reduce((acc, record) => {
        const key = getWriteableDate({ date: record.attendance_date, removeTime: true });
        if (key && key !== "--") {
            acc[key] = record.status;
        }
        return acc;
    }, {});

    const days = [];
    const cursor = moment(startDate).startOf("day");
    const last = moment(endDate).startOf("day");

    while (cursor.isSameOrBefore(last, "day")) {
        const key = cursor.format("YYYY-MM-DD");
        days.push({
            key,
            label: cursor.format("DD-MM-YYYY"),
            status: statusByDate[key] || null,
        });
        cursor.add(1, "day");
    }

    return days;
}

export default function UserBatchAttendance() {
    const { userId } = useOutletContext();
    const { batchId } = useParams();
    const navigate = useNavigate();
    const { requestAPI } = useAppContext();

    const [dateRange, setDateRange] = useState(null);
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [days, setDays] = useState();

    const canFetch = useMemo(() => Array.isArray(dateRange) && dateRange[0] && dateRange[1], [dateRange]);

    const fetchAttendance = useCallback(() => {
        if (!canFetch) {
            return;
        }

        const start_date = getWriteableDate({ date: dateRange[0], removeTime: true });
        const end_date = getWriteableDate({ date: dateRange[1], removeTime: true });

        requestAPI({
            requestPath: `users/${userId}/batches/${batchId}/attendance`,
            requestMethod: "GET",
            requestGetQuery: { start_date, end_date },
            setLoading,
            onRequestStart: () => {
                setError();
                setDays();
            },
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setDays(
                        buildDayBoxes({
                            startDate: start_date,
                            endDate: end_date,
                            records: data.records || [],
                        }),
                    );
                } else {
                    setError(data?.error || "Couldn't load Attendance");
                }
            },
        });
    }, [batchId, canFetch, dateRange, requestAPI, userId]);

    return (
        <div className="flex flex-column h-full">
            <TabHeader
                className="px-3 pt-3"
                title="Attendance"
                highlights={[`Batch #${batchId}`]}
                actionItems={[
                    <Button icon="pi pi-arrow-left" severity="secondary" onClick={() => navigate(-1)} />,
                ]}
            />
            <Divider />

            <div className="px-3 flex align-items-center gap-2 flex-wrap">
                <Calendar
                    value={dateRange}
                    onChange={(e) => setDateRange(e.value)}
                    selectionMode="range"
                    readOnlyInput
                    hideOnRangeSelection
                    dateFormat="dd/mm/yy"
                    placeholder="Select date range"
                    className="flex-1"
                />
                <Button label="Fetch" icon="pi pi-search" severity="warning" disabled={!canFetch || loading} loading={loading} onClick={fetchAttendance} />
            </div>

            <div className="flex-1 overflow-y-scroll p-3">
                {loading ? (
                    <Loading />
                ) : error ? (
                    <NoContent error={error} />
                ) : days ? (
                    days.length ? (
                        <div className="flex flex-wrap gap-2">
                            {days.map((day) => {
                                const isPresent = day.status === "PRESENT";
                                const isAbsent = day.status === "ABSENT";
                                const boxClass = isPresent
                                    ? "bg-green-500 text-white"
                                    : isAbsent
                                      ? "bg-red-500 text-white"
                                      : "bg-gray-200 text-gray-700";

                                return (
                                    <div
                                        key={day.key}
                                        className={`border-1 border-round px-3 py-2 ${TEXT_SMALL} font-semibold ${boxClass}`}
                                        title={day.status || "Not marked"}
                                    >
                                        {day.label}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <NoContent error="No days in selected range" />
                    )
                ) : (
                    <NoContent error="Select a date range and fetch attendance" />
                )}
            </div>

            <div className={`px-3 pb-3 ${TEXT_NORMAL} text-color-secondary`}>
                Green = Present · Red = Absent · Grey = Not marked
            </div>
        </div>
    );
}
